import type { ModeState } from '../lib/api.ts';
import { Chapter, Explain, Points, Shot } from './guide/kit.tsx';
import {
  BalancesMock,
  ExecuteMock,
  LayoutMock,
  LoginMock,
  OpportunitiesMock,
  Phase0Mock,
  SettingsMock,
  TradeLogMock,
  TransfersMock,
} from './guide/mocks.tsx';

/**
 * How to use the dashboard — written for someone who has never seen it.
 *
 * Every screen gets a picture with numbered callouts, a list saying what each
 * number is, and short step-by-step tasks. The pictures are drawn in HTML from
 * the dashboard's own styles (see ./guide/mocks.tsx), with example data.
 *
 * One page, served in two places: as a tab inside the dashboard, and at the
 * public path /guide so it can be shared without handing out a login. The PDF
 * is printed from this same page, so the three can never disagree.
 */

interface Props {
  mode: ModeState | null;
  /** Rendered at /guide without a session — no dashboard chrome around it. */
  publicView?: boolean;
}

const CHAPTERS: Array<[string, string]> = [
  ['start', 'What this tool does'],
  ['sign-in', 'Signing in'],
  ['layout', 'Finding your way'],
  ['opportunities', 'Opportunities'],
  ['phase0', 'Phase 0'],
  ['transfers', 'Transfers'],
  ['balances', 'Balances & feeds'],
  ['trade-log', 'Trade log'],
  ['settings', 'Settings'],
  ['words', 'Key words'],
  ['safety', 'Safety rules'],
];

const chapterNo = (id: string) => CHAPTERS.findIndex(([key]) => key === id) + 1;

const FLOW: Array<{ title: string; text: string; risk?: boolean }> = [
  { title: 'Spot', text: 'The coin is cheaper on exchange A than on exchange B' },
  { title: 'Check', text: 'Fees, balance and receiving address all pass' },
  { title: 'Buy on A', text: 'The buy price is now locked in' },
  { title: 'Send', text: 'The coin travels to B — 2 to 30 minutes' },
  { title: 'Wait', text: 'B’s price can move while the coin travels', risk: true },
  { title: 'Sell on B', text: 'Profit or loss is now final' },
];

const TERMS: Array<[string, string]> = [
  ['Ask', 'The price you pay to buy'],
  ['Bid', 'The price you receive when you sell'],
  ['Spread', 'The difference between bid and ask on one exchange'],
  ['Depth', 'How much can be traded before the price moves against you'],
  ['Gross', 'The raw price gap: (higher − lower) ÷ lower'],
  ['Net', 'What is left after taker fees, withdrawal fee and network fee'],
  ['Taker fee', 'The exchange’s charge for filling an order immediately (~0.1%)'],
  ['Withdrawal fee', 'A flat charge, in the coin, to send it off an exchange'],
  ['Slippage', 'Paying a worse price than expected because the order ate into depth'],
  ['T0 / T+10', 'The moment a gap is spotted / ten minutes later'],
  ['Episode', 'One tracked gap, from the moment it is spotted until 30 minutes later'],
  ['Stale quote', 'A price an exchange keeps showing but no longer honours'],
  ['Ticker collision', 'Two exchanges using the same letters for different coins'],
];

export function Guide({ mode, publicView = false }: Props) {
  const executionOn = mode?.executionEnabled ?? false;
  const transferPct = mode?.transfer?.minNetPct;
  // Links are spelled out in full so they still mean something on paper.
  const site = window.location.origin;
  const host = window.location.host;

  return (
    <div className={publicView ? 'guide guide-public' : 'guide'}>
      <div className="page-head">
        <div>
          <h1>How to use the dashboard</h1>
          <div className="sub">A picture of every screen, what each part means, and how to get things done.</div>
          {/* A printed copy freezes the live status below, so it says when. */}
          <div className="sub print-only">
            Snapshot of {host}/guide taken{' '}
            {new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
          </div>
        </div>
        <div className="row no-print">
          {publicView && (
            <a className="btn" href="/">
              Sign in to the dashboard
            </a>
          )}
          <button className="btn primary" onClick={() => window.print()}>
            Download PDF
          </button>
        </div>
      </div>

      {mode && (
        <div className={`notice ${executionOn ? 'bad' : 'ok'} guide-state`}>
          <b>Right now:</b> {mode.info.label} · trading is{' '}
          <b>{executionOn ? 'ON — real orders can be placed' : 'OFF — watching and measuring only'}</b>
          {mode.pocMeasurement ? ' · Phase 0 measurement running' : ''}
        </div>
      )}

      <nav className="guide-toc" aria-label="Contents">
        {CHAPTERS.map(([id, title], i) => (
          <a key={id} href={`#${id}`}>
            <span className="chapter-n">{i + 1}</span>
            {title}
          </a>
        ))}
      </nav>

      {/* ------------------------------------------------------------ intro */}

      <Chapter id="start" n={chapterNo('start')} title="What this tool does" purpose="The whole idea, in one minute.">
        <Points
          items={[
            'Watches the same coins on several exchanges, every fraction of a second',
            'Spots when a coin is cheaper on one exchange than on another',
            'Works out the profit after every fee — never just the raw gap',
            'Alerts the owner, and can place the trade after the owner confirms',
            'Runs for one owner, trading their own money on their own accounts',
          ]}
        />

        <h3 className="guide-h3">How one transfer trade works</h3>
        <div className="flow" role="img" aria-label="Spot, check, buy on A, send, wait, sell on B">
          {FLOW.map((step, i) => (
            <div key={step.title} className={step.risk ? 'flow-step risk' : 'flow-step'}>
              <b>
                <span className="flow-n">{i + 1}</span>
                {step.title}
              </b>
              {step.text}
            </div>
          ))}
        </div>
        <div className="notice warn">
          Step 5 is the risk. Once the buy fills, the only way back to cash is to sell the coin.
        </div>

        <h3 className="guide-h3">The switches, as they are right now</h3>
        <div className="table-scroll">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Switch</th>
                <th>What it controls</th>
                <th>Now</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sym">Method</td>
                <td>Transfer, or pre-positioned (money kept on both exchanges)</td>
                <td>{mode ? mode.mode : '—'}</td>
              </tr>
              <tr>
                <td className="sym">Trading</td>
                <td>Master switch. While off, nothing is ever bought, sold or sent</td>
                <td className={executionOn ? 'red' : 'green'}>{mode ? (executionOn ? 'ON' : 'OFF') : '—'}</td>
              </tr>
              <tr>
                <td className="sym">Phase 0</td>
                <td>The read-only measurement that tests whether gaps last</td>
                <td>{mode ? (mode.pocMeasurement ? 'running' : 'off') : '—'}</td>
              </tr>
              {transferPct !== undefined && (
                <tr>
                  <td className="sym">Transfer threshold</td>
                  <td>The smallest net profit a transfer gap needs before it is shown</td>
                  <td>{transferPct}%</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Points items={['These are set on the server, not from the dashboard — ask the administrator to change them']} />
      </Chapter>

      {/* ---------------------------------------------------------- sign in */}

      <Chapter
        id="sign-in"
        n={chapterNo('sign-in')}
        title="Signing in"
        purpose={`Open ${host} in any browser. This is the first screen.`}
      >
        <Shot label="Picture of the sign-in screen">
          <LoginMock />
        </Shot>
        <Explain
          legend={[
            ['Username', 'The name the administrator gave you'],
            ['Password', 'The eye icon shows what you typed, to catch typos'],
            ['Two-factor code', 'Only appears after the password, and only if two-factor is switched on'],
            ['Sign in', 'Greyed out until both boxes are filled'],
          ]}
          tasks={[
            {
              title: 'Sign in',
              steps: [
                `Go to ${host}`,
                'Type your username and password',
                'Click Sign in',
                'If a Two-factor code box appears, type the 6-digit code from your authenticator app',
              ],
            },
            {
              title: 'Forgotten your password?',
              steps: [
                'There is no reset e-mail',
                'Ask the administrator — they reset it on the server',
                'Change it again after signing in',
              ],
              bullets: true,
            },
            {
              title: 'Signed out unexpectedly?',
              steps: ['Every update to the system signs everyone out', 'Just sign in again — nothing is lost'],
              bullets: true,
            },
          ]}
        />
      </Chapter>

      {/* ----------------------------------------------------------- layout */}

      <Chapter
        id="layout"
        n={chapterNo('layout')}
        title="Finding your way"
        purpose="The bar along the top is on every screen. It tells you what the system is doing."
      >
        <Shot
          label="Picture of the top bar and status strip"
          caption="The status strip shows the system's real settings, read live when this page loaded."
        >
          <LayoutMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Tabs', 'One per screen. The one you are on is highlighted'],
            ['Feeds', 'How many exchanges are sending prices. 4/4 with a green dot is healthy'],
            ['Sign out', 'Ends your session'],
            ['Method', 'Which way of trading is switched on'],
            [
              'Trading switch',
              <>
                <span className="green">○ detecting only</span> = no orders can be placed.{' '}
                <span className="red">● EXECUTION LIVE</span> = real money
              </>,
            ],
            ['Phase 0 measuring', 'The feasibility test is collecting data in the background'],
            ['Threshold', 'The smallest net profit that gets shown, and the usual transit time'],
          ]}
          tasks={[
            {
              title: 'Check whether the system can spend money',
              steps: [
                'Look at number 5 in the status strip',
                'Green “detecting only” — it cannot place any order',
                'Red “EXECUTION LIVE” — it can trade with real money',
                'Only the administrator can flip this switch',
              ],
              bullets: true,
            },
            {
              title: 'Check the system is healthy',
              steps: [
                'Top right should read 4/4 feeds with a green dot',
                'If it shows fewer, open Balances & feeds to see which exchange dropped',
              ],
            },
          ]}
        />
      </Chapter>

      {/* ---------------------------------------------------- opportunities */}

      <Chapter
        id="opportunities"
        n={chapterNo('opportunities')}
        title="Opportunities"
        purpose="Price gaps that are worth trading right now, after every fee. The list updates by itself."
      >
        <Shot label="Picture of the Opportunities screen with three example rows">
          <OpportunitiesMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Filters', 'Show only gaps above a net %, for one coin or one exchange, and choose the order'],
            ['Counter', 'How many rows pass your filters, out of all live gaps'],
            ['Streaming', 'Green = live prices arriving. Amber “reconnecting” = the list may be out of date'],
            ['Buy on', 'The cheaper exchange, and the price you would pay'],
            ['Sell on', 'The dearer exchange, and the price you would get'],
            ['Net', 'What is left after every fee, in green. Gross, beside it, is the raw gap. Always judge by Net'],
            [
              'Fee drag · Min size · Transit',
              'Share eaten by the withdrawal fee · smallest trade (USDT) that still profits · usual travel time',
            ],
            ['Est. profit', 'Net profit in USDT at the default trade size'],
            ['Age', 'How long ago the gap was spotted. Most close within seconds'],
            ['Execute', 'Opens the confirm box. Turns grey “expired” once the price is too old'],
          ]}
          tasks={[
            {
              title: 'Read one row',
              steps: [
                'The first row says: buy ENA on MEXC at 0.4210, sell it on KuCoin at 0.4302',
                'After every fee that is 1.62%',
                'On a 500 USDT trade that is about 8.10 USDT',
                'The coin takes about 10 minutes to arrive',
              ],
              bullets: true,
            },
            {
              title: 'Find the gaps for one coin',
              steps: [
                'Type the coin in Pair — for example ENA',
                'Set Sort by to net profit',
                'Raise Min net % to hide the small ones',
              ],
            },
            {
              title: 'Why is the list empty?',
              steps: [
                'Empty is the normal state — not a fault, and not the weekend (crypto trades 24/7)',
                'Gaps that clear every fee are rare',
                'On the current exchanges they close in under a second',
                'To see what was found earlier, open the Trade log',
              ],
              bullets: true,
            },
          ]}
        />

        <h3 className="guide-h3">The confirm box — after you click Execute</h3>
        <Shot label="Picture of the trade confirmation box">
          <ExecuteMock />
        </Shot>
        <Explain
          legend={[
            ['Route', 'Where it buys and where it sells, with the prices'],
            ['Net', 'The profit after every fee, as a percentage'],
            ['Trade size', 'How much USDT to spend. Pre-filled from Settings'],
            ['Estimated net profit', 'Recalculated as you change the size'],
            ['Countdown', 'How long this price is still valid. When it runs out, the trade is blocked'],
            ['Confirm', 'Places the trade. Nothing happens until you click it'],
          ]}
          tasks={[
            {
              title: 'Place a trade',
              steps: [
                'Check the status strip says ● EXECUTION LIVE — while it says “detecting only”, every trade is refused',
                'Click Execute on a green row',
                'Check the route and the Net %',
                'Type the trade size — it cannot go above the maximum set in Settings',
                'Check the estimated profit',
                'Click Confirm before the bar runs out',
                'Follow the trade on the Transfers tab',
              ],
              note: 'An amber warning means the size is bigger than what is on offer at that price — the rest would fill at a worse price. A red one means the size is above your maximum.',
            },
          ]}
        />
      </Chapter>

      {/* ---------------------------------------------------------- phase 0 */}

      <Chapter
        id="phase0"
        n={chapterNo('phase0')}
        title="Phase 0"
        purpose="The feasibility test. Does a gap still exist after the minutes a coin spends travelling? Read-only — it never trades."
      >
        <Shot label="Picture of the Phase 0 screen">
          <Phase0Mock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Window (hours)', 'How far back the verdict and the tables look. Starts at 168 = the last 7 days'],
            ['Threshold %', 'The profit a gap must still clear to count as “survived”'],
            ['Refresh', 'Reloads with your new Window and Threshold'],
            ['Totals', 'Counts for the whole run since it started. Not affected by Window'],
            ['Verdict', 'GO, NO-GO or INCONCLUSIVE — the system’s reading of the table below it'],
            ['Still ≥1.5%', 'Of the gaps spotted, the share still paying 1.5% or more that many minutes later. The key number'],
            ['Still ≥0%', 'The share that at least did not lose money'],
            ['Missing', 'No fresh price at that moment — left out, not counted as a win or a loss'],
            ['By coin', 'Which coins produced the gaps, and how long theirs lasted'],
          ]}
          tasks={[
            {
              title: 'See the whole measurement',
              steps: ['Set Window (hours) to 504 — that is 21 days', 'Click Refresh', 'Wait a few seconds for the tables to fill'],
            },
            {
              title: 'Answer: does a gap last long enough?',
              steps: [
                'Find the T+10m row — a transfer takes about 10 minutes',
                'Read the Still ≥1.5% column',
                '“50% (4)” means 4 gaps — half of those measured — still paid 1.5% or more ten minutes later',
              ],
            },
            {
              title: 'Top cards say 1,350 but the table is nearly empty?',
              steps: [
                'The top cards count the whole run',
                'The verdict and tables only count the Window',
                'Widen the Window to include older data',
              ],
              bullets: true,
            },
          ]}
        />
      </Chapter>

      {/* -------------------------------------------------------- transfers */}

      <Chapter
        id="transfers"
        n={chapterNo('transfers')}
        title="Transfers"
        purpose="Follow each trade while the coin is moving between exchanges. Updates itself every 4 seconds."
      >
        <Shot label="Picture of the Transfers screen">
          <TransfersMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Holding — needs a decision', 'Trades that stopped while you still own the coin. Always at the top, in red'],
            ['Step strip', 'buying → bought → sending → in transit → arrived → selling. Green = done, orange = happening now'],
            ['Details', 'Size, expected profit, coins bought, withdrawal ID, coins received, final profit or loss'],
            ['Status', 'COMPLETED, or where it stopped — FAILED, FAILED_HOLDING, TIMED_OUT_HOLDING'],
            ['Deposit addresses', 'Where each coin is sent on the receiving exchange'],
            ['Whitelisted', '“yes” only once the address is approved on the sending exchange’s website'],
          ]}
          tasks={[
            {
              title: 'Follow a trade',
              steps: [
                'After you click Confirm, open the Transfers tab',
                'The orange step shows where the trade is now',
                'It is finished when the status says COMPLETED and a P&L appears',
              ],
            },
            {
              title: 'Deal with a red “Holding” card',
              steps: [
                'Nothing happens automatically from here',
                'Log in to the exchange shown on the card and find the coin',
                'Sell it there, or finish sending it by hand',
                'Tell the administrator',
              ],
            },
            {
              title: 'Add a deposit address',
              steps: [
                'There is no button for this on the screen yet — the administrator adds it',
                'Then approve (whitelist) it on the sending exchange’s website',
                'Without approval the exchange refuses to send — after the coin is already bought',
              ],
              bullets: true,
            },
          ]}
        />
      </Chapter>

      {/* --------------------------------------------------------- balances */}

      <Chapter
        id="balances"
        n={chapterNo('balances')}
        title="Balances & feeds"
        purpose="Two checks on one screen: is the system seeing prices, and where is the money?"
      >
        <Shot label="Picture of the Balances and feeds screen">
          <BalancesMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Refresh', 'Reads the balances from the exchanges again'],
            ['State', 'live (green) = prices arriving. Anything else = that exchange is left out of every calculation'],
            ['Fresh pairs', 'Coins with an up-to-date price, out of all watched. Amber = some are stale'],
            ['Reconnects', 'How often the connection dropped. Last error, beside it, says why'],
            ['Account status', 'connected, error (a key problem), or no keys'],
            ['Free', 'Can be traded now. In orders = locked in open orders. Total = both'],
          ]}
          tasks={[
            {
              title: 'Check prices are flowing',
              steps: ['Every exchange should say live', 'Fresh pairs should be full, for example 118/118'],
            },
            {
              title: 'Check the money on each exchange',
              steps: [
                'Click Refresh',
                'Buying needs USDT on the exchange you buy on',
                'Balances are cached for a short time, so a trade can take a moment to show',
              ],
            },
            {
              title: 'Fix a red “error” card',
              steps: [
                'The key is missing the Read permission — fix it on the exchange’s website',
                'The exchange wants the server’s IP address on the key — ask the administrator for it',
                'The server clock is off — ask the administrator',
              ],
              bullets: true,
            },
          ]}
        />
      </Chapter>

      {/* -------------------------------------------------------- trade log */}

      <Chapter
        id="trade-log"
        n={chapterNo('trade-log')}
        title="Trade log"
        purpose="The permanent record: every trade attempted and every gap spotted. Nothing is ever deleted."
      >
        <Shot label="Picture of the Trade log screen">
          <TradeLogMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            ['Period', 'Last hour, last 24 hours or last 7 days'],
            ['Summary', 'Profit so far · trades left one-sided · gaps spotted · best gap seen'],
            ['Tabs', 'Executed trades (real orders) and Detected opportunities (gaps seen)'],
            ['Lifetime', 'How long the gap stayed open. Amber = under a second, too fast for a person'],
            ['Alerted', 'Whether a Telegram or e-mail alert went out'],
          ]}
          tasks={[
            {
              title: 'See what the system found this week',
              steps: ['Choose last 7 days', 'Click the Detected opportunities tab'],
            },
            {
              title: 'Check real profit',
              steps: [
                'Realized P&L shows the total',
                'The Executed trades tab lists every order — failures too, with the reason',
              ],
              bullets: true,
            },
            {
              title: 'If Unhedged partials is not 0',
              steps: [
                'One side of a trade filled and the other did not',
                'You are holding a coin you did not mean to hold',
                'Sort it out on the exchange, then tell the administrator',
              ],
              bullets: true,
            },
          ]}
        />
      </Chapter>

      {/* --------------------------------------------------------- settings */}

      <Chapter
        id="settings"
        n={chapterNo('settings')}
        title="Settings"
        purpose="The control panel: thresholds, trade sizes, alerts, exchange keys and sign-in security."
      >
        <Shot label="Picture of the Settings screen">
          <SettingsMock mode={mode} />
        </Shot>
        <Explain
          legend={[
            [
              'Detection',
              <>
                Minimum profit, safety buffer, how old a price may be. Applies to the <b>pre-positioned</b> method
                only{transferPct !== undefined ? ` — the transfer threshold (now ${transferPct}%) is set on the server` : ''}
              </>,
            ],
            ['Trade sizing', 'The size pre-filled in the confirm box, and the hard maximum per trade'],
            ['Alerts', 'Switch alerts on · Telegram · e-mail · the lowest % worth an alert · quiet time per route'],
            ['Exchange API keys', 'Each exchange and its key — only the first and last characters are ever shown'],
            ['Save settings', 'Nothing changes until you click it'],
            ['Two-factor authentication', 'Adds a 6-digit code from your phone to every sign-in'],
          ]}
          tasks={[
            {
              title: 'Change a setting',
              steps: [
                'Change the value',
                'Click Save settings',
                'The screen then asks for a detector restart — ask the administrator',
              ],
            },
            {
              title: 'Add or replace an exchange key',
              steps: [
                'On the exchange’s website, create an API key with Read and Trade on, Withdraw off',
                'Limit the key to the server’s IP address (ask the administrator for it)',
                'Here, under Exchange API keys, click Add keys or Replace',
                'Paste the API key and secret — OKX and KuCoin also ask for a passphrase',
                'Click Save keys',
                'Open Balances & feeds — the exchange should say connected',
              ],
            },
            {
              title: 'Switch on two-factor sign-in',
              steps: [
                'Click Set up',
                'In Google Authenticator or Authy, add an account and type in the Secret shown',
                'Type the 6-digit code from the app',
                'Click Verify and enable',
              ],
              note: 'From then on, every sign-in asks for a code. Keep the phone safe.',
            },
            {
              title: 'Get Telegram alerts',
              steps: ['Tick Push alerts enabled and Telegram', 'Click Save settings'],
              note: 'The Telegram bot itself is connected on the server by the administrator.',
            },
          ]}
        />
      </Chapter>

      {/* ------------------------------------------------------------ words */}

      <Chapter id="words" n={chapterNo('words')} title="Key words" purpose="The terms used on the screens.">
        <div className="table-scroll">
          <table className="guide-table">
            <tbody>
              {TERMS.map(([term, meaning]) => (
                <tr key={term}>
                  <td className="sym">{term}</td>
                  <td>{meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Chapter>

      {/* ----------------------------------------------------------- safety */}

      <Chapter
        id="safety"
        n={chapterNo('safety')}
        title="Safety rules"
        purpose="Read these before any real money goes in."
      >
        <h3 className="guide-h3">What an API key can do</h3>
        <div className="table-scroll">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Permission</th>
                <th>Lets the system</th>
                <th>Needed for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="sym">Read</td>
                <td>See balances</td>
                <td>Always</td>
              </tr>
              <tr>
                <td className="sym">Trade</td>
                <td>Buy and sell</td>
                <td>Both methods</td>
              </tr>
              <tr>
                <td className="sym">Withdraw</td>
                <td>Send coins off the exchange</td>
                <td>Transfer method only — switch it on only on the day live trading starts</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="guide-h3">Before going live</h3>
        <Points
          items={[
            'Trading stays off until the measurement says go and every check below is done',
            'Change the sign-in password, then switch on two-factor sign-in',
            'Set up Telegram alerts — they are the only warning when a coin gets stuck',
            'Approve deposit addresses on the sending exchange, not only here',
            'Start with one tiny real trade (around $50) before any real size',
            'Keys are stored encrypted and are never shown in full, anywhere',
          ]}
        />

        <h3 className="guide-h3">Other pages</h3>
        <Points
          items={[
            <>
              <a href={`${site}/methods`}>{host}/methods</a> — the three ways to arbitrage, drawn as diagrams
            </>,
            <>
              <a href={`${site}/ledger`}>{host}/ledger</a> — every tracked trade, with each cost itemised
            </>,
            <>
              <a href={`${site}/guide`}>{host}/guide</a> — this guide, always up to date
            </>,
            'All three are public pages; no sign-in needed',
          ]}
        />
      </Chapter>
    </div>
  );
}
